import { useState, useCallback, useRef, useEffect } from 'react';
import { formatCurrencyInput, parseCurrencyInput } from '@/lib/currencyFormat';

interface CurrencyInputProps {
  value: string | number;
  onChange: (rawValue: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
}

/**
 * Türkçe para formatında input bileşeni.
 *
 * Özellikler:
 * - Yazarken otomatik binlik nokta (her 3 hanede bir)
 * - Ondalık virgül desteği (10.500,50)
 * - İmleç koruması (yazarken imleç zıplamaz)
 * - Kopyala-yapıştır desteği (80000, 80.000, 80.000,50, ₺80.000)
 * - Negatif değer engelleme
 * - Mobilde sayısal klavye (inputMode="decimal")
 * - Boş değer desteği
 * - Veritabanına ham sayısal değer gönderir
 */
export default function CurrencyInput({
  value,
  onChange,
  className = '',
  placeholder = '',
  disabled = false,
  id,
  required,
  min,
  max,
}: CurrencyInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const cursorPosRef = useRef<number | null>(null);
  const lastFormattedRef = useRef('');

  // Raw değeri normalize et
  const rawValue = value !== undefined && value !== null && value !== '' ? String(value) : '';

  // Display value'yi güncelle (dışarıdan value değişirse)
  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formatCurrencyInput(rawValue));
    }
  }, [rawValue, isFocused]);

  /**
   * Bir string'deki belirli bir cursor pozisyonundan önce kaç formatlama karakteri var hesapla
   * (nokta ve virgül)
   */
  const countFormatCharsBefore = (str: string, cursorPos: number): number => {
    let count = 0;
    for (let i = 0; i < Math.min(cursorPos, str.length); i++) {
      if (str[i] === '.' || str[i] === ',') {
        count++;
      }
    }
    return count;
  };

  /**
   * İmleç pozisyonunu ayarla - formatlama değişikliğine göre
   */
  const adjustCursor = useCallback((oldFormatted: string, newFormatted: string, oldCursorPos: number) => {
    if (!inputRef.current) return;

    // Eski pozisyondan önceki format karakterlerini say
    const oldFormatCount = countFormatCharsBefore(oldFormatted, oldCursorPos);
    // İmlecin gerçek (format karakterleri hariç) pozisyonu
    const logicalPos = oldCursorPos - oldFormatCount;

    // Yeni string'de aynı mantıksal pozisyon için imleç nerede olmalı
    let newCursorPos = 0;
    let charsCounted = 0;
    for (let i = 0; i < newFormatted.length; i++) {
      if (newFormatted[i] !== '.' && newFormatted[i] !== ',') {
        if (charsCounted >= logicalPos) break;
        charsCounted++;
      }
      newCursorPos = i + 1;
    }

    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    });
  }, []);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    // Focus olduğunda display value'yi koru (formatlı göster)
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    // Blur'da formatla
    setDisplayValue(formatCurrencyInput(rawValue));
    lastFormattedRef.current = formatCurrencyInput(rawValue);
  }, [rawValue]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const newDisplay = input.value;
    const cursorPos = input.selectionStart || 0;

    // Silme işlemi mi kontrol et (karakter sayısı azalmış)
    const isDeletion = newDisplay.length < displayValue.length;

    // Eğer tamamen boşaltıldıysa
    if (!newDisplay.trim() || !/\d/.test(newDisplay)) {
      onChange('');
      setDisplayValue('');
      lastFormattedRef.current = '';
      return;
    }

    // Ham sayısal değeri parse et
    const parsed = parseCurrencyInput(newDisplay);

    // Negatif değer kontrolü
    if (parsed.startsWith('-')) {
      return; // Negatif değere izin verme
    }

    // Min/Max kontrolü
    if (parsed) {
      const numVal = parseFloat(parsed);
      if (min !== undefined && numVal < min) return;
      if (max !== undefined && numVal > max) return;
    }

    // Formatla
    const formatted = formatCurrencyInput(parsed);

    // İmleç pozisyonunu hesapla
    cursorPosRef.current = cursorPos;
    lastFormattedRef.current = formatted;
    setDisplayValue(formatted);

    // onChange'i ham değerle çağır
    onChange(parsed);

    // İmleci ayarla (render sonrası)
    if (isDeletion) {
      // Silme işleminde imleci uygun yere koy
      setTimeout(() => {
        if (inputRef.current) {
          const newFormattedLen = formatted.length;
          // Silme sonrası imleci doğal pozisyona al
          const adjustedPos = Math.min(cursorPos, newFormattedLen);
          inputRef.current.setSelectionRange(adjustedPos, adjustedPos);
        }
      }, 0);
    } else {
      // Ekleme işleminde imleci format karakterleri sonrasına al
      const oldFormatted = displayValue;
      setTimeout(() => adjustCursor(oldFormatted, formatted, cursorPos), 0);
    }
  }, [displayValue, onChange, min, max, adjustCursor]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    if (!pasted) return;

    const parsed = parseCurrencyInput(pasted);
    if (!parsed) return;

    const numVal = parseFloat(parsed);
    if (isNaN(numVal) || numVal < 0) return;
    if (min !== undefined && numVal < min) return;
    if (max !== undefined && numVal > max) return;

    const formatted = formatCurrencyInput(parsed);
    setDisplayValue(formatted);
    onChange(parsed);

    // İmleci sona al
    setTimeout(() => {
      if (inputRef.current) {
        const len = formatted.length;
        inputRef.current.setSelectionRange(len, len);
      }
    }, 0);
  }, [onChange, min, max]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Eksi tuşunu engelle
    if (e.key === '-' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
    // Ondalık için hem nokta hem virgüle izin ver
    if (e.key === '.') {
      e.preventDefault();
      // Virgül olarak ekle
      const input = e.currentTarget as HTMLInputElement;
      const start = input.selectionStart || 0;
      const current = input.value;

      // Sadece bir ondalık ayırıcı olsun
      if (current.includes(',')) return;

      const newValue = current.slice(0, start) + ',' + current.slice(start);
      const parsed = parseCurrencyInput(newValue);
      const formatted = formatCurrencyInput(parsed);
      setDisplayValue(formatted);
      onChange(parsed);

      setTimeout(() => {
        if (inputRef.current) {
          const commaPos = formatted.indexOf(',');
          if (commaPos >= 0) {
            inputRef.current.setSelectionRange(commaPos + 1, commaPos + 1);
          }
        }
      }, 0);
    }
  }, [onChange]);

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={isFocused ? displayValue : formatCurrencyInput(rawValue)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onPaste={handlePaste}
      onKeyDown={handleKeyDown}
      className={className}
      placeholder={placeholder}
      disabled={disabled}
      id={id}
      required={required}
      autoComplete="off"
    />
  );
}